import React, { useState, useEffect } from 'react';
import CarCard from '../CarCard';

const CarsGrid = ({ cars, isLoading = false }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const carsPerPage = 10; // Maximum 10 cars per page for mobile
  
  // Calculate pagination
  const indexOfLastCar = currentPage * carsPerPage;
  const indexOfFirstCar = indexOfLastCar - carsPerPage;
  const currentCars = cars.slice(indexOfFirstCar, indexOfLastCar);
  const totalPages = Math.ceil(cars.length / carsPerPage);
  
  // For desktop, show all cars; for mobile, show paginated
  const displayCars = isMobile ? currentCars : cars;
  const showPagination = isMobile && totalPages > 1;
  
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top when changing page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  useEffect(() => {
    setCurrentPage(1);
  }, [cars]);
  
  // Detect mobile and handle resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Reset page when switching between mobile/desktop
      if (mobile !== isMobile) {
        setCurrentPage(1);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobile]);
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 lg:gap-6">
        {[...Array(6)].map((_, index) => (
          <div key={index} className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 animate-pulse">
            <div className="bg-slate-200 h-48 rounded-xl mb-4"></div>
            <div className="bg-slate-200 h-6 rounded-lg mb-2"></div>
            <div className="bg-slate-200 h-4 rounded-lg w-3/4"></div>
          </div>
        ))}
      </div>
    );
  }

  if (cars.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="bg-slate-100 rounded-full p-6 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
          <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-slate-700 mb-3">
          No se encontraron vehículos
        </h3>
        <p className="text-slate-500 text-lg">
          Intenta ajustar los filtros de búsqueda
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 lg:gap-5">
        {displayCars.map((car, index) => (
          <div key={car.id}>
            <CarCard car={car} />
            {/* Mobile separator - only show on mobile and not for last item */}
            {index < displayCars.length - 1 && (
              <div className="sm:hidden">
                <div className="border-t-2 border-slate-300 my-3 opacity-60"></div>
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Pagination for mobile */}
      {showPagination && (
        <div className="flex justify-center items-center mt-6 space-x-3">
          <button
            onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              currentPage === 1
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
            }`}
          >
            Anterior
          </button>
          
          <span className="text-sm text-slate-600 font-medium min-w-[60px] text-center">
            {currentPage} de {totalPages}
          </span>
          
          <button
            onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              currentPage === totalPages
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
            }`}
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
};

export default CarsGrid;
